import React, { useState, useEffect, useRef } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { Package, GitCommit, Clock, CheckCircle, XCircle, AlertCircle, Loader, ChevronDown, ChevronUp, RefreshCcw } from 'lucide-react';

export default function Deployments() {
  // Activer/désactiver les logs de debug
  const DEBUG_LOGS = true;
  // Parse le paramètre opts quand il est fourni en chaîne YAML (clé: valeur par ligne)
  const parseOptsParam = (optsRaw) => {
    if (!optsRaw) return {};
    if (typeof optsRaw === 'object' && !Array.isArray(optsRaw)) return optsRaw;
    if (typeof optsRaw === 'string') {
      const obj = {};
      optsRaw.split(/\r?\n/).forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) return;
        const idx = trimmed.indexOf(':');
        if (idx === -1) return;
        const key = trimmed.slice(0, idx).trim();
        const val = trimmed.slice(idx + 1).trim();
        if (key) obj[key] = val;
      });
      return obj;
    }
    return {};
  };
  const [envsData, setEnvsData] = useState({});
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionCommits, setVersionCommits] = useState({});
  const [commitsLoading, setCommitsLoading] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deploymentHistory, setDeploymentHistory] = useState([]);
  const historyRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Configuration des environnements
  // Pour ajouter un nouvel environnement, ajouter une entrée avec:
  // key: identifiant (ex: 'QA2'), valeurs: { name, pipelineId, releaseDefinitionId, repos, version }
  const envConfig = {
    'dev01': {
      name: 'dev01',
      pipelineId: 320,
      releaseDefinitionId: 150,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'QA1': {
      name: 'QA1',
      pipelineId: 557,
      releaseDefinitionId: 160,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'QA2': {
      name: 'QA2',
      pipelineId: 326,
      releaseDefinitionId: 161,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'QA3': {
      name: 'QA3',
      pipelineId: 560,
      releaseDefinitionId: 162,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'QA4': {
      name: 'QA4',
      pipelineId: 572,
      releaseDefinitionId: 163,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'val05': {
      name: 'val05',
      pipelineId: 357,
      releaseDefinitionId: 167,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'agents01': {
      name: 'agents01',
      pipelineId: 509,
      releaseDefinitionId: 199,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'agents02': {
      name: 'agents02',
      pipelineId: 561,
      releaseDefinitionId: 209,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'val06': {
      name: 'val06',
      pipelineId: 387,
      releaseDefinitionId: 168,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: '25.1'
    },
    'val07': {
      name: 'val07',
      pipelineId: 342,
      releaseDefinitionId: 169,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: '25.1'
    },
    'val10': {
      name: 'val10',
      pipelineId: 376,
      releaseDefinitionId: 173,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: '25.1'
    },
    'val9': {
      name: 'val9',
      pipelineId: 401,
      releaseDefinitionId: 155,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'develop'
    },
    'perf1': {
      name: 'perf1',
      pipelineId: 508,
      releaseDefinitionId: 109,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'releases/25/25.2'
    },
    'dev4': {
      name: 'dev4',
      pipelineId: 351,
      releaseDefinitionId: 104,
      repos: ['argonos', 'argonos-front','shared-devops'],
      version: 'releases/25/25.2'
    }
  };

  useEffect(() => {
    // Charger les données de tous les environnements au démarrage
    loadAllEnvironments();

    // Polling pour rafraîchir l'état (build/deploy en cours)
    const interval = setInterval(() => {
      silentRefreshAllEnvironments();
    }, 20000); // toutes les 20s
    return () => clearInterval(interval);
  }, []);

  const loadAllEnvironments = async () => {
    setLoading(true);
    const data = {};
    
    for (const envKey of Object.keys(envConfig)) {
      const config = envConfig[envKey];
      try {
        const envData = await fetchEnvironmentData(config);
        if (DEBUG_LOGS) console.log('[Deployments] Loaded env', config.name, envData);
        data[envKey] = envData;
      } catch (error) {
        console.error(`Erreur chargement ${envKey}:`, error);
        data[envKey] = { error: error.message };
      }
    }
    
    setEnvsData(data);
    setLoading(false);
  };

  // Rafraîchissement silencieux (sans spinner) pour mettre à jour les statuts
  const silentRefreshAllEnvironments = async () => {
    const current = { ...envsData };
    for (const envKey of Object.keys(envConfig)) {
      const config = envConfig[envKey];
      try {
        const envData = await fetchEnvironmentData(config);
        current[envKey] = envData;
      } catch (e) {
        console.warn('Refresh échoué pour', envKey, e);
      }
    }
    setEnvsData(current);
  };

  const fetchEnvironmentData = async (config) => {
    const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    // 1. Récupérer les releases liées à la définition
    let releases = [];
    try {
      releases = await azureDevOpsService.getReleasesForDefinition(config.releaseDefinitionId, 20);
    } catch (e) {
      console.error('Erreur récupération releases:', e);
    }
    if (DEBUG_LOGS) console.log('[Deployments] Releases fetched for', config.name, 'count=', releases.length);

    // Trouver l'environnement correspondant dans chaque release (matching tolérant)
    const mappedEnvReleases = releases.map(r => {
      const envRec = (r.environments || []).find(e => {
        const en = normalize(e.name);
        const cn = normalize(config.name);
        return en === cn || en.includes(cn) || cn.includes(en);
      });
      return { release: r, env: envRec };
    }).filter(m => m.env);
    if (DEBUG_LOGS) console.log('[Deployments] Matched releases for env', config.name, '=>', mappedEnvReleases.length);

    const latestEnvRelease = mappedEnvReleases[0] || null;
    const lastSuccessfulEnvRelease = mappedEnvReleases.find(m => m.env.status?.toLowerCase() === 'succeeded') || null;
    let overallStatus = latestEnvRelease ? (latestEnvRelease.env.status || 'unknown') : 'unknown';

    // 2. Build pipeline (queueTime pour date dernier déploiement même échoué)
    let buildWithTimeline = null;
    let latestPipelineBuild = null;
    try {
      latestPipelineBuild = await azureDevOpsService.getLatestBuildForPipeline(config.pipelineId);
      if (latestPipelineBuild) {
        buildWithTimeline = await azureDevOpsService.getBuildWithTimeline(latestPipelineBuild.id);
      }
    } catch (e) {
      console.warn('Impossible de récupérer le build timeline:', e);
    }
    if (DEBUG_LOGS) {
      console.log('[Deployments] Latest pipeline build for', config.name, '=>', latestPipelineBuild?.id, latestPipelineBuild?.status, latestPipelineBuild?.result);
      const paramsPreview = (buildWithTimeline?.parameters && typeof buildWithTimeline.parameters === 'string')
        ? (buildWithTimeline.parameters.length > 500 ? buildWithTimeline.parameters.slice(0, 500) + '…' : buildWithTimeline.parameters)
        : buildWithTimeline?.parameters;
      console.log('[Deployments] BuildWithTimeline status/result', buildWithTimeline?.status, buildWithTimeline?.result, 'deployStage=', buildWithTimeline?.deployStage?.name, buildWithTimeline?.deployStage?.state, 'params=', paramsPreview);
    }

    // Date de dernier lancement pipeline (queueTime) ou dernière activité env release
    const envDeploymentTimestamp = latestEnvRelease?.env?.modifiedOn || latestEnvRelease?.env?.createdOn || latestEnvRelease?.release?.createdOn;
    const lastDeploymentDate = buildWithTimeline?.queueTime || envDeploymentTimestamp || null;

    // Surclasser le statut global par le dernier run pipeline réel (plus précis)
    if (buildWithTimeline) {
      const buildResult = (buildWithTimeline.result || buildWithTimeline.status || '').toLowerCase();
      const deployStageResult = (buildWithTimeline.deployStage ? (buildWithTimeline.deployStage.result || buildWithTimeline.deployStage.state) : '').toLowerCase();
      if (buildResult === 'failed' || deployStageResult === 'failed') {
        overallStatus = 'failed';
      } else if (buildResult === 'succeeded' && (deployStageResult === '' || deployStageResult === 'succeeded')) {
        overallStatus = 'succeeded';
      } else if (buildResult) {
        overallStatus = buildResult;
      }
    }

    // 3. Build number depuis artifact
    const refReleaseForBuild = (lastSuccessfulEnvRelease?.release) || (latestEnvRelease?.release);
    const buildArtifact = refReleaseForBuild?.artifacts?.find(a => a.type === 'Build');
    const rawBuildNumber = latestPipelineBuild?.buildNumber 
      || buildArtifact?.definitionReference?.version?.name
      || buildArtifact?.definitionReference?.version?.id
      || buildArtifact?.definitionReference?.version?.value
      || refReleaseForBuild?.name
      || 'N/A';

    // Extraction: garder motif AAAAMMJJ-n ou dernier bloc date-format
    const shortMatch = rawBuildNumber.match(/(\d{8}-\d+)/);
    const buildShortNumber = shortMatch ? shortMatch[1] : (rawBuildNumber.split('-').filter(s => /\d+/.test(s)).slice(-2).join('-') || rawBuildNumber);

    // 4. Durées build / déploiement
    const buildStart = buildWithTimeline?.startTime ? new Date(buildWithTimeline.startTime) : null;
    const buildFinish = buildWithTimeline?.finishTime ? new Date(buildWithTimeline.finishTime) : null;
    const buildDurationMs = (buildStart && buildFinish) ? (buildFinish - buildStart) : null;

    const deployStage = buildWithTimeline?.deployStage;
    const deployStart = deployStage?.startTime ? new Date(deployStage.startTime) : null;
    const deployFinish = deployStage?.finishTime ? new Date(deployStage.finishTime) : null;
    const deployDurationMs = (deployStart && deployFinish) ? (deployFinish - deployStart) : null;

    // 5. Branches dynamiques détectées depuis le run pipeline (paramètres Back/Front) puis fallback release artifacts
    const dynamicBranches = {};
    // 5.a Depuis les templateParameters du pipeline run (paramètres runtime manuels)
    try {
      if (latestPipelineBuild?.id) {
        const pipelineRun = await azureDevOpsService.getPipelineRun(config.pipelineId, latestPipelineBuild.id);
        if (DEBUG_LOGS) console.log('[Deployments] Pipeline run fetched for', config.name, 'templateParameters=', pipelineRun?.templateParameters);
        const templateParams = pipelineRun?.templateParameters || {};
        const optsObj = parseOptsParam(templateParams.opts);

        // Chercher d'abord dans opts branch_devops / opts_k8s (chaîne ou objet)
        if (optsObj.branch_devops && typeof optsObj.branch_devops === 'string') {
          dynamicBranches['shared-devops'] = optsObj.branch_devops.replace(/^refs\/heads\//i, '');
          if (DEBUG_LOGS) console.log('[Deployments] Template param from opts.branch_devops => shared-devops:', dynamicBranches['shared-devops']);
        }
        if (optsObj.branch_k8s && typeof optsObj.branch_k8s === 'string') {
          dynamicBranches['shared-devops'] = optsObj.branch_k8s.replace(/^refs\/heads\//i, '');
          if (DEBUG_LOGS) console.log('[Deployments] Template param from opts.branch_k8s => shared-devops:', dynamicBranches['shared-devops']);
        }
        // Vérifier aussi opts_k8s directement dans templateParams (cas param racine)
        if (templateParams.opts_k8s && typeof templateParams.opts_k8s === 'string') {
          dynamicBranches['shared-devops'] = templateParams.opts_k8s.replace(/^refs\/heads\//i, '');
          if (DEBUG_LOGS) console.log('[Deployments] Template param from opts_k8s => shared-devops:', dynamicBranches['shared-devops']);
        }
        
        const entries = Object.entries(templateParams);
        for (const [key, val] of entries) {
          if (!val || typeof val !== 'string') continue;
          const keyLower = key.toLowerCase();
          // Filtrer les paramètres qui ressemblent à des branches (contient "branch", "opts_", ou repo name)
          const isBranchParam = /branch|opts_/i.test(key) || config.repos.some(r => keyLower.includes(r.toLowerCase()));
          if (!isBranchParam) continue;
          
          const normalized = val.replace(/^refs\/heads\//i, '');
          
          if (keyLower.includes('front')) {
            dynamicBranches['argonos-front'] = normalized;
            if (DEBUG_LOGS) console.log('[Deployments] Template param mapped', key, '=> argonos-front:', normalized);
          } else if (keyLower.includes('back') || keyLower.includes('api') || keyLower.includes('argonos')) {
            dynamicBranches['argonos'] = normalized;
            if (DEBUG_LOGS) console.log('[Deployments] Template param mapped', key, '=> argonos:', normalized);
          } else if (config.repos.some(r => keyLower.includes(r.toLowerCase()))) {
            const repoMatch = config.repos.find(r => keyLower.includes(r.toLowerCase()));
            dynamicBranches[repoMatch] = normalized;
            if (DEBUG_LOGS) console.log('[Deployments] Template param mapped', key, '=>', repoMatch, ':', normalized);
          } else {
            // Afficher quand même, sous l'étiquette du paramètre
            dynamicBranches[key] = normalized;
            if (DEBUG_LOGS) console.log('[Deployments] Template param unmapped, keeping', key, ':', normalized);
          }
        }
      }
    } catch (e) {
      console.warn('Impossible de récupérer les templateParameters du pipeline run:', e);
    }

    // 5.b Fallback: build.parameters (ancien système, JSON string)
    if (Object.keys(dynamicBranches).length === 0) {
      try {
        const paramsRaw = buildWithTimeline?.parameters;
        if (paramsRaw && typeof paramsRaw === 'string') {
          if (DEBUG_LOGS) console.log('[Deployments] Fallback to build.parameters (JSON):', paramsRaw);
          const paramsObj = JSON.parse(paramsRaw);
          const entries = Object.entries(paramsObj || {});
          for (const [key, val] of entries) {
            if (!val || typeof val !== 'string') continue;
            if (!/branch/i.test(key)) continue;
            const normalized = val.replace(/^refs\/heads\//i, '');
            const keyLower = key.toLowerCase();
            if (keyLower.includes('front')) {
              dynamicBranches['argonos-front'] = normalized;
            } else if (keyLower.includes('back') || keyLower.includes('api') || keyLower.includes('argonos')) {
              dynamicBranches['argonos'] = normalized;
            } else if (config.repos.some(r => keyLower.includes(r.toLowerCase()))) {
              const repoMatch = config.repos.find(r => keyLower.includes(r.toLowerCase()));
              dynamicBranches[repoMatch] = normalized;
            } else {
              dynamicBranches[key] = normalized;
            }
          }
        }
      } catch (e) {
        console.warn('Impossible de parser les paramètres du build pour les branches:', e);
      }
    }

    // 5.c Fallback: via artifacts de release (si multi-artifacts)
    if (Object.keys(dynamicBranches).length === 0) {
      // Si aucun release env mappé, essayer le dernier release brut
      const releaseForArtifacts = refReleaseForBuild || releases[0] || null;
      if (releaseForArtifacts?.artifacts && Array.isArray(releaseForArtifacts.artifacts)) {
        if (DEBUG_LOGS) console.log('[Deployments] Using artifacts fallback for branches, artifacts=', releaseForArtifacts.artifacts?.map(a => ({ alias: a.alias, name: a.name, type: a.type })));
        for (const art of releaseForArtifacts.artifacts) {
          if (art.type === 'Build') {
            const alias = art.alias || art.name || 'artifact';
            const defRef = art.definitionReference || {};
            const branchNode = defRef.branch || defRef.Branch || defRef.sourceBranch || defRef.branchName;
            let branchFromDefRef = branchNode?.name || branchNode?.id || branchNode?.value || null;
            if (branchFromDefRef && typeof branchFromDefRef === 'string') {
              branchFromDefRef = branchFromDefRef.replace(/^refs\/heads\//, '');
            }
            if (!branchFromDefRef && defRef.version?.id) {
              // fallback via build API
              const buildId = defRef.version.id;
              const buildDetails = await azureDevOpsService.getBuild(buildId);
              if (buildDetails) {
                branchFromDefRef = (buildDetails.sourceBranch || '').replace(/^refs\/heads\//, '') || null;
                if (DEBUG_LOGS) console.log('[Deployments] Artifact via build API', alias, 'build', buildId, 'branch', branchFromDefRef);
              }
            }
            if (branchFromDefRef) {
              const matchedRepo = config.repos.find(r => alias?.toLowerCase()?.includes(r.toLowerCase())) || alias;
              dynamicBranches[matchedRepo] = branchFromDefRef;
              if (DEBUG_LOGS) console.log('[Deployments] Artifact', alias, '-> repo', matchedRepo, 'branch', branchFromDefRef);
            }
          }
        }
      }
    }
    // Fallback si aucune branche trouvée => utiliser config.version pour chaque repo
    if (Object.keys(dynamicBranches).length === 0) {
      config.repos.forEach(r => { dynamicBranches[r] = config.version; });
      if (DEBUG_LOGS) console.log('[Deployments] No branches found; falling back to envConfig.version for repos', config.repos, 'version=', config.version);
    }
    if (DEBUG_LOGS) console.log('[Deployments] Final dynamicBranches for', config.name, dynamicBranches);

    return {
      config,
      latestRelease: latestEnvRelease?.release || null,
      lastSuccessfulRelease: lastSuccessfulEnvRelease?.release || null,
      overallStatus,
      buildNumber: rawBuildNumber,
      buildShortNumber,
      lastDeploymentDate,
      buildDurationMs,
      deployDurationMs,
      build: buildWithTimeline,
      dynamicBranches
    };
  };

  const loadDeploymentHistory = async (envKey) => {
    if (selectedEnv === envKey) {
      // Si on clique sur la même card, on ferme (pas de scroll)
      setSelectedEnv(null);
      setDeploymentHistory([]);
      return;
    }

    setSelectedEnv(envKey);
    setLoadingHistory(true);
    setSelectedVersion(null);
    
    const config = envConfig[envKey];
    
    try {
      // Récupérer l'historique des builds avec timeline (les 20 derniers)
      const builds = await azureDevOpsService.getBuildsWithTimeline(config.pipelineId, 20);
      setDeploymentHistory(builds);
      
      // Auto-scroll vers l'historique après un court délai
      setTimeout(() => {
        historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      setDeploymentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadCommitsForVersion = async (build, config) => {
    if (selectedVersion === build.id) {
      // Si on clique sur la même version, on ferme
      setSelectedVersion(null);
      setCommitsLoading((prev) => ({ ...prev, [build.id]: false }));
      return;
    }

    // Vérifier si le déploiement est en échec (build ou deploy)
    const isFailed = 
      build.buildStatus?.toLowerCase() === 'failed' || 
      build.deployStatus?.toLowerCase() === 'failed';
    
    if (isFailed) {
      // Ne pas charger les commits pour les déploiements échoués
      setSelectedVersion(build.id);
      setCommitsLoading((prev) => ({ ...prev, [build.id]: false }));
      setVersionCommits({ ...versionCommits, [build.id]: null });
      return;
    }

    setSelectedVersion(build.id);
    setCommitsLoading((prev) => ({ ...prev, [build.id]: true }));
    
    // Récupérer les commits depuis le dernier déploiement en succès
    const commits = {};
    
    // Trouver le dernier build en succès ou partiellement réussi
    const currentIndex = deploymentHistory.findIndex(b => b.id === build.id);
    let lastSuccessfulBuild = null;
    
    for (let i = currentIndex + 1; i < deploymentHistory.length; i++) {
      const prevBuild = deploymentHistory[i];
      const buildStatus = prevBuild.buildStatus?.toLowerCase();
      const deployStatus = prevBuild.deployStatus?.toLowerCase();
      
      // Chercher succeeded ou partiellement réussis
      const prevBuildSuccess = buildStatus === 'succeeded' || buildStatus === 'partiallysucceeded';
      const prevDeploySuccess = !deployStatus || deployStatus === 'succeeded' || deployStatus === 'succeededwithissues';
      
      if (prevBuildSuccess && prevDeploySuccess) {
        lastSuccessfulBuild = prevBuild;
        break;
      }
    }
    
    // Récupérer les commits réellement déployés dans le build précédent pour les exclure
    let previousBuildCommitIds = new Set();
    if (lastSuccessfulBuild) {
      try {
        const prevChanges = await azureDevOpsService.getBuildChanges(lastSuccessfulBuild.id);
        // Les changes peuvent avoir 'id' ou 'commitId' selon l'API
        previousBuildCommitIds = new Set(
          (prevChanges || [])
            .map(c => (c.id || c.commitId || '').toLowerCase())
            .filter(Boolean)
        );
        if (DEBUG_LOGS) {
          console.log(
            `[Deployments] Build ${lastSuccessfulBuild.id}: ${previousBuildCommitIds.size} commits à exclure (ex: ${Array.from(previousBuildCommitIds).slice(0, 5).join(', ')})`
          );
        }
      } catch (error) {
        console.warn('Impossible de récupérer les commits du build précédent:', error);
      }
    }
    
    // Dates de la plage: utiliser finishTime du build précédent pour éviter chevauchement
    const toDate = new Date(build.queueTime).toISOString();
    let fromDate;
    if (lastSuccessfulBuild) {
      // Utiliser finishTime pour exclure complètement la période du build précédent
      const referenceTime = lastSuccessfulBuild.finishTime || lastSuccessfulBuild.queueTime;
      fromDate = new Date(referenceTime).toISOString();
    } else {
      fromDate = new Date(new Date(build.queueTime).getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    }
    
    let buildBranches = {};
    try {
      // Récupérer les branches dynamiques de ce build spécifique
      const pipelineRun = await azureDevOpsService.getPipelineRun(config.pipelineId, build.id);
      const templateParams = pipelineRun?.templateParameters || {};
      const optsObj = parseOptsParam(templateParams.opts);
      
      // Chercher d'abord dans opts branch_devops / branch_k8s (chaîne ou objet)
      if (optsObj.branch_devops && typeof optsObj.branch_devops === 'string') {
        buildBranches['shared-devops'] = optsObj.branch_devops.replace(/^refs\/heads\//i, '');
      }
      if (optsObj.branch_k8s && typeof optsObj.branch_k8s === 'string') {
        buildBranches['shared-devops'] = optsObj.branch_k8s.replace(/^refs\/heads\//i, '');
      }
      // Vérifier aussi opts_k8s directement dans templateParams (cas param racine)
      if (templateParams.opts_k8s && typeof templateParams.opts_k8s === 'string') {
        buildBranches['shared-devops'] = templateParams.opts_k8s.replace(/^refs\/heads\//i, '');
      }
      
      // Puis parcourir les autres paramètres
      for (const [key, val] of Object.entries(templateParams)) {
        if (!val || typeof val !== 'string') continue;
        const keyLower = key.toLowerCase();
        const isBranchParam = /branch|opts_/i.test(key) || config.repos.some(r => keyLower.includes(r.toLowerCase()));
        if (!isBranchParam) continue;
        const normalized = val.replace(/^refs\/heads\//i, '');
        if (keyLower.includes('front')) {
          buildBranches['argonos-front'] = normalized;
        } else if (keyLower.includes('back') || keyLower.includes('api') || keyLower.includes('argonos')) {
          buildBranches['argonos'] = normalized;
        } else if (keyLower.includes('shared') || keyLower.includes('devops')) {
          buildBranches['shared-devops'] = normalized;
        } else if (config.repos.some(r => keyLower.includes(r.toLowerCase()))) {
          const repoMatch = config.repos.find(r => keyLower.includes(r.toLowerCase()));
          buildBranches[repoMatch] = normalized;
        }
      }
    } catch (e) {
      console.warn('Impossible de récupérer les branches du build pour commits:', e);
    }
    
    // Fallback: utiliser config.version si aucune branche détectée
    if (Object.keys(buildBranches).length === 0) {
      config.repos.forEach(r => { buildBranches[r] = config.version; });
    }
    
    try {
      for (const repoName of config.repos) {
        const branchForRepo = buildBranches[repoName] || config.version;
        try {
          const repoCommits = await azureDevOpsService.getCommitsForRepoByDateRange(
            repoName,
            fromDate,
            toDate,
            branchForRepo
          );
          
          // Filtrer pour exclure les commits du build précédent (comparaison insensible à la casse)
          const filteredCommits = repoCommits.filter(commit => {
            const commitId = (commit.commitId || commit.id || '').toLowerCase();
            const isExcluded = commitId && previousBuildCommitIds.has(commitId);
            if (isExcluded && DEBUG_LOGS) {
              console.log(`[Deployments] Exclusion commit ${commitId.substring(0, 7)} de ${repoName}`);
            }
            return !isExcluded;
          });
          
          if (DEBUG_LOGS) {
            console.log(`[Deployments] ${repoName}: ${repoCommits.length} commits récupérés, ${filteredCommits.length} après filtrage`);
          }
          commits[repoName] = filteredCommits;
        } catch (error) {
          console.error(`Erreur récupération commits pour ${repoName} (branche: ${branchForRepo}):`, error);
          commits[repoName] = [];
        }
      }
      setVersionCommits({ ...versionCommits, [build.id]: commits });
    } finally {
      setCommitsLoading((prev) => ({ ...prev, [build.id]: false }));
    }
  };

  const getStatusIcon = (status, size = 'h-5 w-5') => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
        return <CheckCircle className={`${size} text-green-500`} />;
      case 'failed':
        return <XCircle className={`${size} text-red-500`} />;
      case 'inprogress':
        return <Loader className={`${size} text-blue-500 animate-spin`} />;
      case 'partiallysucceeded':
      case 'partiallysuceeded':
        return <AlertCircle className={`${size} text-yellow-500`} />;
      default:
        return <AlertCircle className={`${size} text-gray-500`} />;
    }
  };

  const STATUS_FR = {
    succeeded:          'Réussi',
    failed:             'Échoué',
    inprogress:         'En cours',
    partiallysucceeded: 'Partiellement réussi',
    partiallysuceeded:  'Partiellement réussi',
    canceled:           'Annulé',
    cancelling:         'Annulation…',
    unknown:            'Inconnu',
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    let bgColor = 'bg-gray-100 text-gray-700';
    
    if (statusLower === 'succeeded') bgColor = 'bg-green-100 text-green-700';
    else if (statusLower === 'failed') bgColor = 'bg-red-100 text-red-700';
    else if (statusLower === 'inprogress') bgColor = 'bg-blue-100 text-blue-700';
    else if (statusLower === 'partiallysucceeded' || statusLower === 'partiallysuceeded') bgColor = 'bg-yellow-100 text-yellow-700';
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${bgColor}`}>
        {STATUS_FR[statusLower] || status || 'N/A'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (ms) => {
    if (!ms && ms !== 0) return 'N/A';
    const secs = Math.floor(ms / 1000);
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    if (minutes === 0) return `${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  // Filtrer les environnements
  const filteredEnvs = Object.entries(envsData).filter(([envKey, data]) => {
    // Filtre par statut
    if (statusFilter !== 'all') {
      const status = data.overallStatus?.toLowerCase();
      if (statusFilter === 'succeeded' && status !== 'succeeded') return false;
      if (statusFilter === 'failed' && status !== 'failed') return false;
      if (statusFilter === 'inprogress' && status !== 'inprogress') return false;
      if (statusFilter === 'partiallySucceeded' && status !== 'partiallySucceeded') return false;
    }
    
    // Filtre par date
    if (dateFilter !== 'all' && data.lastDeploymentDate) {
      const deployDate = new Date(data.lastDeploymentDate);
      const now = new Date();
      const diffHours = (now - deployDate) / (1000 * 60 * 60);
      
      if (dateFilter === '24h' && diffHours > 24) return false;
      if (dateFilter === '7d' && diffHours > 24 * 7) return false;
      if (dateFilter === '30d' && diffHours > 24 * 30) return false;
    }
    
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-6">Déploiements</h1>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Statut:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              <option value="succeeded">Succès</option>
              <option value="failed">Échec</option>
              <option value="inprogress">En cours</option>
              <option value="partiallySucceeded">Partiellement réussi</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Date de déploiement:</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes</option>
              <option value="24h">Dernières 24h</option>
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
            </select>
          </div>
          
          <div className="ml-auto text-sm text-gray-600">
            {filteredEnvs.length} / {Object.keys(envsData).length} environnement(s)
          </div>
        </div>
      </div>

      {/* Environment Cards Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : filteredEnvs.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Aucun environnement ne correspond aux filtres sélectionnés</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {filteredEnvs.map(([envKey, data]) => (
            <div 
              key={envKey}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-500"
              onClick={() => loadDeploymentHistory(envKey)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">{data.config.name}</h2>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${data.overallStatus?.toLowerCase() === 'succeeded' ? 'bg-green-100 text-green-700' : data.overallStatus?.toLowerCase() === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {data.overallStatus?.toLowerCase() === 'succeeded' ? 'Succès' : data.overallStatus?.toLowerCase() === 'failed' ? 'Échec' : data.overallStatus}
                  </span>
                </div>
                <div className="flex justify-end -mt-2 mb-2">
                  <button
                    className="text-xs flex items-center text-gray-500 hover:text-blue-600"
                    onClick={(e) => { e.stopPropagation(); silentRefreshAllEnvironments(); }}
                    title="Rafraîchir"
                  >
                    <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
                  </button>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  {/* Indicateur déploiement en cours */}
                  {(() => {
                    const deployStageState = (data.build?.deployStage?.state || '').toLowerCase();
                    const deployStageResult = (data.build?.deployStage?.result || '').toLowerCase();
                    const buildStatus = (data.build?.status || '').toLowerCase();
                    const deployInProgress = deployStageState === 'inprogress' || (deployStageResult === '' && buildStatus === 'inprogress' && deployStageState !== 'completed');
                    const buildInProgress = buildStatus === 'inprogress' && !deployInProgress; // build encore en cours avant étape deploy
                    if (deployInProgress) {
                      return (
                        <div className="flex items-center justify-between bg-blue-50 px-2 py-1 rounded">
                           <a
                             href={data.build?._links?.web?.href || (data.build?.id ? `https://dev.azure.com/chapsvision/Fabrique/_build/results?buildId=${data.build.id}` : '#')}
                             target={data.build?._links?.web?.href ? '_blank' : undefined}
                             rel="noopener noreferrer"
                             onClick={(e) => e.stopPropagation()}
                             className="text-blue-600 font-medium flex items-center hover:underline"
                           >
                             <Loader className="h-4 w-4 animate-spin mr-2" /> Déploiement en cours…
                           </a>
                           <span className="text-[10px] text-blue-500 uppercase tracking-wide">LIVE</span>
                         </div>
                      );
                    } else if (buildInProgress) {
                      return (
                        <div className="flex items-center justify-between bg-yellow-50 px-2 py-1 rounded">
                          <span className="text-yellow-700 font-medium flex items-center">
                            <Loader className="h-4 w-4 animate-spin mr-2" /> Build en cours…
                          </span>
                          <span className="text-[10px] text-yellow-600 uppercase tracking-wide">LIVE</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div>
                    <span className="text-gray-600 block mb-1">Branches:</span>
                    <div className="ml-2 space-y-1">
                      {Object.entries(data.dynamicBranches || {})
                        .filter(([repo]) => repo === 'argonos' || repo === 'argonos-front' || repo === 'shared-devops')
                        .sort(([repoA], [repoB]) => {
                          // Trier: argonos en premier, puis argonos-front, puis shared-devops
                          const order = { 'argonos': 1, 'argonos-front': 2, 'shared-devops': 3 };
                          const a = order[repoA] || 99;
                          const b = order[repoB] || 99;
                          return a - b;
                        })
                        .map(([repo, branch]) => (
                          <div key={repo} className="flex items-center gap-2 text-xs">
                            <span className="font-medium text-gray-700 min-w-[100px]">{repo}:</span>
                            <span className="text-blue-600 font-mono bg-blue-50 px-2 py-0.5 rounded">{branch || 'N/A'}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dernier déploiement:</span>
                    <span className="font-medium">{data.lastDeploymentDate ? formatDate(data.lastDeploymentDate) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                     <span className="text-gray-600">Build:</span>
                     {data.build ? (
                       <a
                         href={data.build._links?.web?.href || `https://dev.azure.com/chapsvision/Fabrique/_build/results?buildId=${data.build.id}`}
                         target="_blank"
                         rel="noopener noreferrer"
                         onClick={(e) => e.stopPropagation()}
                         className="font-medium text-blue-600 hover:underline"
                       >
                         {data.buildShortNumber}
                       </a>
                     ) : (
                       <span className="font-medium">{data.buildShortNumber}</span>
                     )}
                   </div>
                  <div className="flex justify-between" title="Durée totale du build">
                    <span className="text-gray-600">Durée build:</span>
                    <span className="font-medium">{formatDuration(data.buildDurationMs)}</span>
                  </div>
                  <div className="flex justify-between" title="Durée de l'étape de déploiement (si présente)">
                    <span className="text-gray-600">Durée déploiement:</span>
                    <span className="font-medium">{formatDuration(data.deployDurationMs)}</span>
                  </div>
                </div>

                {/* Deploy Button */}
                <button 
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    alert('Fonctionnalité de déploiement à implémenter');
                  }}
                >
                  Déployer
                </button>

                {/* Expand Indicator */}
                <div className="flex justify-center mt-4">
                  {selectedEnv === envKey ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Deployment History Section */}
      {selectedEnv && (
        <div ref={historyRef} className="bg-white p-6 rounded-lg shadow mt-6">
          <h2 className="text-2xl font-bold mb-4">Historique de Déploiement - {envConfig[selectedEnv].name}</h2>
          
          {loadingHistory ? (
            <div className="flex justify-center items-center h-32">
              <Loader className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : deploymentHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Build</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Build Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deploy Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {deploymentHistory.map(build => (
                    <React.Fragment key={build.id}>
                      <tr 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => loadCommitsForVersion(build, envConfig[selectedEnv])}
                      >
                        <td className="px-4 py-3 text-sm">
                          <a 
                            href={build._links?.web?.href || `https://dev.azure.com/chapsvision/Fabrique/_build/results?buildId=${build.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {build.buildNumber}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(build.queueTime)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center">
                            {getStatusIcon(build.buildStatus)}
                            <span className="ml-2">{getStatusBadge(build.buildStatus)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center">
                            {build.deployStatus ? (
                              <>
                                {getStatusIcon(build.deployStatus)}
                                <span className="ml-2">{getStatusBadge(build.deployStatus)}</span>
                              </>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {selectedVersion === build.id ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </td>
                      </tr>
                      
                      {/* Commits Details */}
                      {selectedVersion === build.id && commitsLoading[build.id] && (
                        <tr>
                          <td colSpan="5" className="px-4 py-4 bg-gray-50">
                            <div className="flex items-center text-gray-600">
                              <Loader className="h-4 w-4 mr-2 animate-spin" />
                              <span className="font-medium">Chargement des commits…</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {selectedVersion === build.id && versionCommits[build.id] === null && (
                        <tr>
                          <td colSpan="5" className="px-4 py-4 bg-red-50">
                            <div className="flex items-center text-red-600">
                              <XCircle className="h-5 w-5 mr-2" />
                              <span className="font-medium">Déploiement échoué - Aucun commit affiché</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {selectedVersion === build.id && versionCommits[build.id] && versionCommits[build.id] !== null && (
                        <tr>
                          <td colSpan="5" className="px-4 py-4 bg-gray-50">
                            <div className="space-y-4">
                              <h3 className="font-semibold flex items-center">
                                <GitCommit className="h-5 w-5 mr-2 text-green-500" />
                                Commits Inclus (depuis le dernier déploiement réussi)
                              </h3>
                              {Object.entries(versionCommits[build.id]).map(([repoName, commits]) => (
                                <div key={repoName}>
                                  <h4 className="font-medium text-sm text-gray-700 mb-2">{repoName}</h4>
                                  {commits.length > 0 ? (
                                    <div className="space-y-2 ml-4">
                                      {commits.map(commit => (
                                        <div key={commit.commitId} className="border-l-2 border-green-300 pl-3 py-1">
                                          <div className="text-sm">
                                            <span className="font-mono text-xs text-gray-500">
                                              {commit.commitId.substring(0, 7)}
                                            </span>
                                            <span className="ml-2">{commit.comment}</span>
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {commit.author.name} - {formatDate(commit.author.date)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500 ml-4">Aucun commit trouvé</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center">Aucun historique de déploiement trouvé</p>
          )}
        </div>
      )}
    </div>
  );
}
